/**
 * Compliance Hub Guardian — "Report a Problem / AI Help" widget.
 * Floating button on every main page. Collects diagnostics, gets an AI
 * explanation + approved safe fixes, executes them, records results and
 * takes resolved / partial / unresolved feedback.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getRecentEvents, recordEvent } from "@/lib/guardianLog";
import { flushOfflineQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";
import {
  ShieldAlert, X as XIcon, Loader2, RotateCw, RefreshCw, LogIn,
  ArrowRight, CheckCircle2, HelpCircle, Send, History,
} from "lucide-react";

const FIX_ICONS = { retry_sync: RotateCw, restore_autosave: History, reload_page: RefreshCw,
  reconnect_session: LogIn, go_to: ArrowRight, check_fields: CheckCircle2 };

const moduleFromPath = (p) => {
  if (p.startsWith("/inspections") && p.includes("photo-report")) return "Photo Report";
  if (p.startsWith("/inspections") && p.includes("audit")) return "Audit Log";
  if (p.startsWith("/inspections")) return "Inspection Checklist";
  if (p.startsWith("/statutory")) return "Statutory Deadlines";
  if (p.startsWith("/admin/builder")) return "Checklist Builder";
  if (p.startsWith("/admin")) return "Admin";
  if (p.startsWith("/settings")) return "Settings";
  if (p.startsWith("/team")) return "Team";
  if (p === "/") return "Dashboard";
  return p;
};

const GuardianOpener = ({ onOpen }) => {
  useEffect(() => {
    const h = () => onOpen();
    window.addEventListener("ch-open-guardian", h);
    return () => window.removeEventListener("ch-open-guardian", h);
  }, [onOpen]);
  return null;
};

export const GuardianWidget = () => {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("form"); // form | loading | result | feedback | done
  const [desc, setDesc] = useState("");
  const [resp, setResp] = useState(null);
  const [fixBusy, setFixBusy] = useState("");
  const [fixDone, setFixDone] = useState({});
  const loc = useLocation();
  const navigate = useNavigate();

  const collectContext = () => {
    const inspMatch = loc.pathname.match(/\/inspections\/([a-f0-9-]+)/);
    const autosaveEl = document.querySelector('[data-testid="autosave-state"]');
    return {
      page: loc.pathname,
      module: moduleFromPath(loc.pathname),
      action: "",
      inspection_id: inspMatch ? inspMatch[1] : null,
      error_messages: getRecentEvents().filter((e) => e.type !== "info").slice(-6).map((e) => e.message),
      recent_events: getRecentEvents(),
      user_agent: navigator.userAgent,
      online: navigator.onLine,
      sync_state: autosaveEl ? autosaveEl.getAttribute("title") || autosaveEl.textContent : "",
      last_saved_at: null,
      app_version: "1.0",
      screen: `${window.innerWidth}x${window.innerHeight}`,
    };
  };

  const submit = async () => {
    if (desc.trim().length < 3) { toast.error("Please describe what you were trying to do"); return; }
    setPhase("loading");
    // Work protection: snapshot current work before anything else.
    recordEvent("info", "guardian checkpoint created");
    window.dispatchEvent(new Event("guardian:checkpoint"));
    try {
      const r = await api.post("/guardian/report", { description: desc, context: collectContext() });
      setResp(r.data);
      setPhase("result");
    } catch (e) {
      toast.error("Couldn't reach the support service — your report was kept on this device. Please try again.");
      setPhase("form");
    }
  };

  const runFix = async (fix) => {
    setFixBusy(fix.id);
    let result = "ok";
    try {
      if (fix.id === "retry_sync") {
        const n = (await flushOfflineQueue()).flushed;
        window.dispatchEvent(new Event("online"));
        result = `retried sync (${n} pending item${n === 1 ? "" : "s"} flushed)`;
        toast.success("Sync retried — pending saves pushed to the cloud");
      } else if (fix.id === "restore_autosave" || fix.id === "reload_page" || fix.id === "check_fields") {
        result = "page refreshed to restore latest autosave";
        await api.post(`/guardian/incidents/${resp.incident_id}/fix-result`,
          { fix_id: fix.id, label: fix.label, result });
        window.location.reload();
        return;
      } else if (fix.id === "reconnect_session") {
        result = "redirected to sign-in with work preserved";
        await api.post(`/guardian/incidents/${resp.incident_id}/fix-result`,
          { fix_id: fix.id, label: fix.label, result });
        const returnTo = loc.pathname + loc.search;
        window.location.href = `/login?returnTo=${encodeURIComponent(returnTo)}`;
        return;
      } else if (fix.id === "go_to" && fix.path) {
        navigate(fix.path);
        result = `navigated to ${fix.path}`;
      }
    } catch (e) {
      result = `failed: ${e?.message || "unknown"}`;
      toast.error("That fix didn't work — you can tell us below");
    }
    try {
      await api.post(`/guardian/incidents/${resp.incident_id}/fix-result`,
        { fix_id: fix.id, label: fix.label, result });
    } catch { /* audit best-effort */ }
    setFixDone((d) => ({ ...d, [fix.id]: result.startsWith("failed") ? "failed" : "ok" }));
    setFixBusy("");
  };

  const sendFeedback = async (outcome) => {
    try {
      await api.post(`/guardian/incidents/${resp.incident_id}/feedback`, { outcome });
    } catch { /* noop */ }
    if (outcome === "resolved") {
      toast.success("Great — glad it's sorted!");
      close();
    } else {
      setPhase("done");
    }
  };

  const close = () => { setOpen(false); setPhase("form"); setDesc(""); setResp(null); setFixDone({}); };

  return (
    <>
      {/* Launcher lives inside the Ask Poolie menu (SupportChat) — opened via
          a window event so there is only ONE floating button on screen. */}
      <GuardianOpener onOpen={() => setOpen(true)} />

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6" data-testid="guardian-panel-overlay">
          <div className="absolute inset-0 bg-slate-900/70" onClick={close}/>
          <div className="relative w-full max-w-lg bg-white border-2 border-slate-900 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="guardian-panel">
            <div className="px-5 py-4 border-b-2 border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 bg-[#1E1B4B] text-[#00F2C3] inline-flex items-center justify-center"><ShieldAlert size={18} strokeWidth={2.5}/></div>
                <div>
                  <h2 className="font-black text-slate-900 leading-tight">Compliance Hub Guardian</h2>
                  <p className="text-[11px] text-slate-500">AI support &amp; issue resolution</p>
                </div>
              </div>
              <button onClick={close} data-testid="guardian-close" className="h-9 w-9 inline-flex items-center justify-center border-2 border-slate-300 hover:border-slate-900"><XIcon size={16}/></button>
            </div>

            <div className="p-5">
              {phase === "form" && (
                <>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600">What were you trying to do when the problem happened?</label>
                  <textarea
                    value={desc} onChange={(e) => setDesc(e.target.value)}
                    data-testid="guardian-description"
                    rows={4} autoFocus
                    placeholder="e.g. I was saving my inspection at 3 Lucas Ave and the photos wouldn't upload…"
                    className="mt-2 w-full border-2 border-slate-300 p-3 text-sm focus:outline-none focus:border-[#1E1B4B]"
                  />
                  <p className="mt-2 text-[11px] text-slate-500">
                    Guardian will automatically include: current page ({moduleFromPath(loc.pathname)}), recent app events,
                    connection status ({navigator.onLine ? "online" : "offline"}), and your device info. Your work is
                    checkpointed before any fix is attempted. No passwords or payment details are ever collected.
                  </p>
                  <button onClick={submit} data-testid="guardian-submit"
                    className="mt-4 w-full h-12 inline-flex items-center justify-center gap-2 bg-[#1E1B4B] text-white font-bold text-sm hover:bg-[#0F0E2E]">
                    <Send size={15} strokeWidth={2.5}/>Get help now
                  </button>
                </>
              )}

              {phase === "loading" && (
                <div className="py-10 flex flex-col items-center text-slate-600" data-testid="guardian-loading">
                  <Loader2 size={28} className="animate-spin text-[#1E1B4B]"/>
                  <p className="mt-3 text-sm font-bold">Checkpointing your work &amp; diagnosing…</p>
                </div>
              )}

              {(phase === "result" || phase === "feedback") && resp && (
                <>
                  <div className="p-4 bg-teal-50 border-2 border-teal-200 text-sm text-slate-800 leading-relaxed" data-testid="guardian-explanation">
                    {resp.explanation}
                  </div>
                  <p className="mt-2 text-[11px] text-slate-500">
                    Incident <strong>{resp.incident_number}</strong> created — the administrator has been notified.
                  </p>
                  {(resp.suggested_fixes || []).length > 0 && (
                    <div className="mt-4 space-y-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Safe fixes I can try for you</p>
                      {resp.suggested_fixes.map((f) => {
                        const Icon = FIX_ICONS[f.id] || HelpCircle;
                        const done = fixDone[f.id];
                        return (
                          <button key={f.id} onClick={() => runFix(f)} disabled={!!fixBusy || done === "ok"}
                            data-testid={`guardian-fix-${f.id}`}
                            className={`w-full h-11 px-3 inline-flex items-center gap-2.5 border-2 font-bold text-sm text-left transition-colors ${
                              done === "ok" ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                              : done === "failed" ? "border-red-300 bg-red-50 text-red-700"
                              : "border-slate-300 text-slate-900 hover:border-[#1E1B4B] hover:bg-[#00F2C3]/10"}`}>
                            {fixBusy === f.id ? <Loader2 size={15} className="animate-spin"/> : <Icon size={15} strokeWidth={2.5}/>}
                            {f.label}{done === "ok" ? " — done ✓" : done === "failed" ? " — didn't work" : ""}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className="mt-5 border-t-2 border-slate-100 pt-4">
                    <p className="text-sm font-black text-slate-900">Has this resolved your problem?</p>
                    <div className="mt-2 grid grid-cols-1 gap-2">
                      <button onClick={() => sendFeedback("resolved")} data-testid="guardian-feedback-resolved"
                        className="h-11 border-2 border-emerald-600 bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700">Yes, the problem is resolved</button>
                      <button onClick={() => sendFeedback("partial")} data-testid="guardian-feedback-partial"
                        className="h-11 border-2 border-amber-400 bg-white text-amber-800 font-bold text-sm hover:bg-amber-50">Partly resolved</button>
                      <button onClick={() => sendFeedback("unresolved")} data-testid="guardian-feedback-unresolved"
                        className="h-11 border-2 border-slate-300 bg-white text-slate-900 font-bold text-sm hover:border-slate-900">No, I still need support</button>
                    </div>
                  </div>
                </>
              )}

              {phase === "done" && (
                <div className="py-8 text-center" data-testid="guardian-escalated">
                  <CheckCircle2 size={32} className="mx-auto text-[#1E1B4B]"/>
                  <p className="mt-3 font-black text-slate-900">Sent to the administrator</p>
                  <p className="mt-1 text-sm text-slate-600 max-w-sm mx-auto">
                    Your incident {resp?.incident_number} has been escalated with all the technical details —
                    you won&apos;t need to repeat anything. You can keep working; your work is safe.
                  </p>
                  <button onClick={close} data-testid="guardian-done-close"
                    className="mt-4 h-11 px-6 bg-[#1E1B4B] text-white font-bold text-sm hover:bg-[#0F0E2E]">Close</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GuardianWidget;
